import type {
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";
import type { ChangeEvent, FocusEvent } from "react";
import {
  DESCRIPTION_TEXT_INPUT_FILTER,
  LABEL_TEXT_INPUT_FILTER,
  PERSON_NAME_INPUT_FILTER,
  TITLE_TEXT_INPUT_FILTER,
  normalizeTextInput,
} from "@/utils/zodString";

function bindTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(
  field: ControllerRenderProps<TFieldValues, TName>,
  inputFilter?: RegExp
) {
  return {
    ...field,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = event.target.value.replace(/^\s+/, "");
      if (inputFilter) {
        value = value.replace(inputFilter, "");
      }
      field.onChange(value);
    },
    onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const normalized = normalizeTextInput(event.target.value);
      if (normalized !== event.target.value) {
        field.onChange(normalized);
      }
      field.onBlur();
    },
  };
}

/** Normalizes text: no leading spaces while typing; full trim on blur. */
export function bindTrimmedTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return bindTextField(field);
}

/** Class / benefit title fields — letters required; digits allowed. */
export function bindTitleTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return bindTextField(field, TITLE_TEXT_INPUT_FILTER);
}

/** Person name fields — letters, spaces, hyphen, apostrophe only. */
export function bindPersonNameTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return bindTextField(field, PERSON_NAME_INPUT_FILTER);
}

/** Level / catalog label fields — letters required; digits allowed. */
export function bindLabelTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return bindTextField(field, LABEL_TEXT_INPUT_FILTER);
}

/** Description fields — letters, numbers, and sentence punctuation. */
export function bindDescriptionTextField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return bindTextField(field, DESCRIPTION_TEXT_INPUT_FILTER);
}

export function bindLabelTextInputHandlers(
  value: string,
  onChange: (value: string) => void,
  onBlur?: () => void
) {
  return {
    value,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let next = event.target.value.replace(/^\s+/, "");
      next = next.replace(LABEL_TEXT_INPUT_FILTER, "");
      onChange(next);
    },
    onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const normalized = normalizeTextInput(event.target.value);
      if (normalized !== event.target.value) {
        onChange(normalized);
      }
      onBlur?.();
    },
  };
}
